<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useAttachmentUpload } from './composables/useAttachmentUpload';
import { useToast } from '@/components/ui/toast';
import { filterEditorTags, getCaretCoordinates } from './utils/material-utils';

// Composables
import { useEditorDrag } from './composables/useEditorDrag';
import { useEditorClipboard } from './composables/useEditorClipboard';
import { useEditorContent } from './composables/useEditorContent';
import { useEditorNodes } from './composables/useEditorNodes';

// Components
import TagMenu from './editor/TagMenu.vue';
import CommandMenu from './editor/CommandMenu.vue';
import ColorPopover from './editor/ColorPopover.vue';
import MaterialPopover from './editor/MaterialPopover.vue';
import SkillPopover from './editor/SkillPopover.vue';
import SkillSelectorPopover from './editor/SkillSelectorPopover.vue';
import { cn } from './utils/utils';

// Icons
import IconImage from './icons/IconImage.vue';
import IconDocument from './icons/IconDocument.vue';
import IconColor from './icons/IconColor.vue';
import IconClear from './icons/IconClear.vue';
import IconSkill from './icons/IconSkill.vue';

// Toast notification
const { toast } = useToast();

const props = defineProps({
  placeholderPrefix: {
    type: String,
    default: 'Let Veeso AI design ',
  },
  modelValue: {
    type: String,
    default: '',
  },
  class: {
    type: String,
    default: '',
  },
  placeholderClass: {
    type: String,
    default: '',
  },
  placeholderLabels: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  chatState: {
    type: Object,
    default: null,
  },
  minHeight: {
    type: Number,
    default: 96,
  },
  maxHeight: {
    type: Number,
    default: 0,
  },
  multiline: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'paste',
  'update:canvas-size',
  'focus',
  'blur',
  'document-uploaded',
]);

// Material upload
const { isUploading, uploadProgress, uploadMaterial, uploadDocument, validateFile } =
  useAttachmentUpload();

const editorRef = ref(null);
const currentLabelIndex = ref(0);
const typedLabel = ref('');
const isTyping = ref(true);
let typewriterTimeout = null;
const isFocused = ref(false);

const internalMaxHeight = ref(600);
const maxHeight = computed(() => props.maxHeight || internalMaxHeight.value);

const suppressExternalModelSync = ref(false);

const syncContentProxy = (opts) => {
  if (suppressExternalModelSync.value) return;
  syncContent(opts);
};

// Material limits
const MAX_IMAGES = 20;
const MAX_DOCUMENTS = 5;
const MAX_TAGS = 10;
const MAX_COLORS = 10;

// Tag system
const showTagMenu = ref(false);
const tagQuery = ref('');
const selectedTagIndex = ref(0);
const tagMenuPosition = ref(null);

// Command Menu System
const showCommandMenu = ref(false);
const commandQuery = ref('');
const selectedCommandIndex = ref(0);
const commandMenuPosition = ref({ x: 0, y: 0 });

const commands = computed(() => [
  {
    id: 'skill',
    label: 'Add Skill',
    description: 'Add a user skill',
    shortcut: 'Skl',
    icon: IconSkill,
  },
  {
    id: 'image',
    label: 'Upload Image',
    description: 'Upload an image',
    shortcut: 'Img',
    icon: IconImage,
  },
  {
    id: 'document',
    label: 'Upload Document',
    description: 'Upload a document',
    shortcut: 'Doc',
    icon: IconDocument,
  },
  {
    id: 'color',
    label: 'Insert Color',
    description: 'Add a color',
    shortcut: 'Col',
    icon: IconColor,
  },
  {
    id: 'clear',
    label: 'Clear All',
    description: 'Clear editor content',
    shortcut: 'Clr',
    icon: IconClear,
  },
]);

const filteredCommands = computed(() => {
  if (!commandQuery.value) return commands.value;
  const q = commandQuery.value.toLowerCase();
  return commands.value.filter(
    (c) => c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
  );
});

// Skill selector system
const showSkillSelector = ref(false);
const skillSelectorPosition = ref({ x: 0, y: 0 });

/**
 * Handle skill selection from popover
 */
const handleSkillSelect = (skill) => {
  insertUserSkillTag({ id: skill.id });
  showSkillSelector.value = false;
};

const handleCommandSelect = (command) => {
  showCommandMenu.value = false;

  // Store position before removing trigger
  const pos = { ...commandMenuPosition.value };

  // Remove the '/' trigger text
  const selection = window.getSelection();
  if (selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;
    if (textNode.nodeType === Node.TEXT_NODE) {
      const text = textNode.textContent;
      // We look for the last slash before cursor that triggered this
      // Note: This is a simplification, might need more robust matching if user typed a lot
      // Capture the current cursor offset before we modify the range
      const endOffset = range.startOffset;
      const textBefore = text.slice(0, endOffset);
      const lastSlash = textBefore.lastIndexOf('/');

      if (lastSlash !== -1) {
        range.setStart(textNode, lastSlash);
        // Important: use the captured offset, because range.startOffset changes after setStart!
        range.setEnd(textNode, endOffset);
        range.deleteContents();
        // Collapse range to be sure
        range.collapse(true);
        // Sync selection
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  // Execute command
  switch (command.id) {
    case 'skill':
      skillSelectorPosition.value = pos;
      showSkillSelector.value = true;
      break;
    case 'image': {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.png,.jpg,.jpeg,.webp';
      input.onchange = async (e) => {
        if (e.target.files?.length) {
          await uploadImageOnly(e.target.files[0]);
        }
      };
      input.click();
      break;
    }
    case 'document': {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx,.txt,.md';
      input.onchange = async (e) => {
        if (e.target.files?.length) {
          const file = e.target.files[0];
          await uploadDocumentOnly(file);
        }
      };
      input.click();
      break;
    }
    case 'color':
      insertColorTag('#000000');
      break;
    case 'clear':
      setValue('');
      break;
  }
};

// Available tags for the menu
const availableTags = computed(() => filterEditorTags(tagQuery.value));

// Color popover system
const showColorPopover = ref(false);
const colorPopoverData = ref({
  id: '',
  value: '#000000',
  position: { x: 0, y: 0 },
});

/**
 * Handle color tag click - show color editor popover
 */
const handleColorClick = (colorData, element) => {
  const rect = element.getBoundingClientRect();
  colorPopoverData.value = {
    id: colorData.id,
    value: colorData.value,
    position: {
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    },
  };
  showColorPopover.value = true;
};

// Material popover system
const showMaterialPopover = ref(false);
const materialPopoverData = ref({
  id: '',
  url: '',
  tag: '',
  isMaterial: true,
  position: { x: 0, y: 0 },
});

/**
 * Handle material tag click - show material editor popover
 */
const handleMaterialClick = (materialData, element) => {
  const rect = element.getBoundingClientRect();
  materialPopoverData.value = {
    id: materialData.id,
    url: materialData.url,
    tag: materialData.tag || '',
    isMaterial: materialData.isMaterial !== false, // default to true
    element,
    position: {
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    },
  };
  showMaterialPopover.value = true;
};

// Skill popover system
const showSkillPopover = ref(false);
const skillPopoverData = ref({
  id: '',
  position: { x: 0, y: 0 },
});

/**
 * Handle skill tag click - show skill detail popover
 */
const handleSkillClick = (skillData, element) => {
  const rect = element.getBoundingClientRect();
  skillPopoverData.value = {
    id: skillData.id,
    position: {
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8,
    },
  };
  showSkillPopover.value = true;
};

// HEX color regex: matches #RGB or #RRGGBB (followed by space indicates complete input)
const HEX_COLOR_REGEX = /#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})\s$/;

// Remix tag regex: @VeesoRemix('UUID', 'NAME')
const REMIX_TAG_REGEX = /@VeesoRemix\('([^']+)',\s*'([^']+)'\)$/;

// User Skill tag regex: @VeesoUserSkill('UUID')
const USER_SKILL_TAG_REGEX = /@VeesoUserSkill\('([^']+)'\)$/;

// ==================== Asset State Management ====================

// Track asset IDs that were inserted into the editor DOM via addAssetToChatState.
// Only these should be removed by syncAssetsWithEditor when their DOM nodes disappear.
// Assets added externally (Attach component, uploadImageOnly) are NOT tracked here.
const editorManagedAssetIds = new Set();

/**
 * Unified asset add entry (adds to chatState.assets)
 * Assets added through this function are considered "editor-managed" —
 * they have a corresponding DOM node in the editor and will be cleaned up
 * when that node is removed.
 */
const addAssetToChatState = (asset, { checkDuplicate = false } = {}) => {
  if (!props.chatState) return;
  if (!props.chatState.assets) {
    props.chatState.assets = [];
  }
  if (checkDuplicate) {
    const exists = props.chatState.assets.some((a) => a.id === asset.id);
    if (exists) return;
  }
  asset.editorManaged = true;
  props.chatState.assets.push(asset);
  editorManagedAssetIds.add(asset.id);
};

/**
 * Unified asset remove entry (removes from chatState.assets)
 */
const removeAssetFromChatState = (assetId) => {
  if (!props.chatState?.assets) return;
  const index = props.chatState.assets.findIndex((a) => a.id === assetId);
  if (index > -1) {
    props.chatState.assets.splice(index, 1);
  }
  editorManagedAssetIds.delete(assetId);
};

/**
 * Clear all assets (clears chatState.assets)
 */
const clearAllAssetsFromChatState = () => {
  if (!props.chatState) return;
  props.chatState.assets = [];
  editorManagedAssetIds.clear();
};

/**
 * Sync chatState.assets with actual material nodes in editor.
 * Only removes assets that were inserted into the editor DOM (editor-managed)
 * but whose DOM nodes no longer exist. Assets added externally
 * (via Attach component or uploadImageOnly) are never touched.
 */
const syncAssetsWithEditor = () => {
  if (!props.chatState?.assets?.length || !editorRef.value) return;

  // Get all material node IDs currently in editor DOM
  const editorAssetIds = new Set(
    Array.from(editorRef.value.querySelectorAll('[data-type="image"]'))
      .map((node) => node.getAttribute('data-asset-id'))
      .filter(Boolean)
  );

  props.chatState.assets = props.chatState.assets.filter((asset) => {
    // Reference images are always kept
    if (asset.isMaterial === false) return true;
    // Assets not managed by the editor (external uploads) are always kept
    if (!editorManagedAssetIds.has(asset.id)) return true;
    // Editor-managed assets are only kept if their DOM node still exists
    return editorAssetIds.has(asset.id);
  });

  // Clean up tracking for assets that were removed
  for (const id of editorManagedAssetIds) {
    if (!props.chatState.assets.some((a) => a.id === id)) {
      editorManagedAssetIds.delete(id);
    }
  }
};

/**
 * Get current material counts
 */
const getCurrentCounts = () => {
  if (!editorRef.value) return { images: 0, documents: 0, tags: 0, colors: 0 };
  return {
    images: editorRef.value.querySelectorAll('[data-type="image"]').length,
    documents: editorRef.value.querySelectorAll('[data-type="document"]').length,
    tags: editorRef.value.querySelectorAll('[data-type="tag"]').length,
    colors: editorRef.value.querySelectorAll('[data-type="color"]').length,
  };
};

/**
 * Check if can add more materials
 */
const canAddMore = (type) => {
  const counts = getCurrentCounts();
  switch (type) {
    case 'image':
      return counts.images < MAX_IMAGES;
    case 'document':
      return counts.documents < MAX_DOCUMENTS;
    case 'tag':
      return counts.tags < MAX_TAGS;
    case 'color':
      return counts.colors < MAX_COLORS;
    default:
      return true;
  }
};

// ==================== Height Management ====================

/**
 * Calculate available max height (responsive)
 */
const calculateMaxHeight = () => {
  if (props.maxHeight) return;
  const availableHeight = window.innerHeight - 300;
  internalMaxHeight.value = Math.max(availableHeight, 200);
};

/**
 * Auto adjust height (synchronous for better responsiveness)
 */
const adjustHeight = () => {
  if (props.loading || !editorRef.value) return;

  editorRef.value.style.height = 'auto';
  const contentHeight = editorRef.value.scrollHeight;
  const targetHeight = Math.max(props.minHeight, Math.min(contentHeight, maxHeight.value));
  editorRef.value.style.height = `${targetHeight}px`;
};

// ==================== Initialize Composables ====================

// Drag composable
const {
  dragState,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  makeDraggable,
} = useEditorDrag({
  editorRef,
  syncContent: syncContentProxy,
  onFileDrop: handleFileDrop,
});

// Content composable
const {
  isEmpty,
  lastSyncedValue,
  checkEmpty,
  getFormattedContent,
  syncContent,
  parseAndInsertText: parseAndInsertTextBase,
} = useEditorContent({
  editorRef,
  chatState: props.chatState,
  emit,
  adjustHeight,
  syncAssetsWithEditor,
});

// Nodes composable
const {
  insertTextAtCursor,
  insertImageNode,
  createImageElement,
  updateImageNode,
  removeImageNodeById,
  insertTag: insertTagBase,
  insertColorTagNode,
  insertColorTag,
  insertColorTagFromInput: insertColorTagFromInputBase,
  insertRemixTag,
  insertRemixTagFromInput: insertRemixTagFromInputBase,
  insertUserSkillTag,
  insertUserSkillTagFromInput: insertUserSkillTagFromInputBase,
  updateMaterialTag,
  updateColorTag,
  removeColorTag,
  removeUserSkillTag,
  insertDocumentTag,
  updateDocumentNode,
  updateDocumentParsed,
} = useEditorNodes({
  editorRef,
  syncContent: syncContentProxy,
  makeDraggable,
  addAssetToChatState,
  removeAssetFromChatState,
  canAddMore,
  toast,
  limits: { MAX_IMAGES, MAX_DOCUMENTS, MAX_TAGS, MAX_COLORS },
  onColorClick: handleColorClick,
  onMaterialClick: handleMaterialClick,
  onSkillClick: handleSkillClick,
});

// Wrapper for insertTag
const insertTag = (tag) => {
  insertTagBase(tag, { hideMenu: () => (showTagMenu.value = false) });
};

// Wrapper for insertColorTagFromInput
const insertColorTagFromInput = (textNode, range, match) => {
  insertColorTagFromInputBase(textNode, range, match, () => (showTagMenu.value = false));
};

// Wrapper for insertRemixTagFromInput
const insertRemixTagFromInput = (textNode, range, match) => {
  insertRemixTagFromInputBase(textNode, range, match);
};

// Handle color popover update
const handleColorUpdate = ({ id, value }) => {
  updateColorTag(id, value);
};

// Handle color popover delete
const handleColorDelete = (id) => {
  removeColorTag(id);
};

// Handle material popover update
const handleMaterialUpdate = ({ id, tag, isMaterial }) => {
  const element = materialPopoverData.value.element;
  updateMaterialTag(id, tag, isMaterial, element);

  // Update isMaterial in chatState.assets
  if (props.chatState?.assets) {
    const asset = props.chatState.assets.find((a) => a.id === id);
    if (asset) {
      asset.isMaterial = isMaterial;
    }
  }
};

// Handle skill popover remove
const handleSkillRemove = (id) => {
  removeUserSkillTag(id);
};

// Wrapper for parseAndInsertText
const parseAndInsertText = (text, options = {}) => {
  parseAndInsertTextBase(text, {
    ...options,
    insertTextAtCursor,
    insertImageNode,
    insertColorTagNode,
    insertRemixTag,
    insertUserSkillTag,
    insertDocumentTag,
    addAssetToChatState,
  });
};

// ==================== Image Upload ====================

/**
 * Read file as Blob URL
 */
const readFileAsBlobURL = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const url = URL.createObjectURL(file);
      resolve(url);
    } catch (err) {
      reject(err);
    }
  });
};

const resolveImageDimensions = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

/**
 * Insert image (upload as material)
 */
const insertImage = async (file, insertionPoint = null) => {
  // Check material count limit
  if (!canAddMore('image')) {
    toast({
      title: 'Image limit reached',
      description: `You can add up to ${MAX_IMAGES} images`,
      variant: 'destructive',
    });
    return;
  }

  const tempId = `temp_${Date.now()}`;
  // Always use blob url as default image source
  const blobUrl = URL.createObjectURL(file);
  const dims = await resolveImageDimensions(blobUrl);

  // Check if need to add newline before image
  if (editorRef.value && !insertionPoint) {
    const selection = window.getSelection();
    let range;
    if (!selection.rangeCount || !editorRef.value.contains(selection.anchorNode)) {
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
    } else {
      range = selection.getRangeAt(0);
    }

    const { startContainer, startOffset } = range;
    const node = startContainer;
    if (node.nodeType === Node.TEXT_NODE) {
      const textContent = node.textContent;
      if (startOffset > 0 && textContent) {
        const charBefore = textContent[startOffset - 1];
        if (charBefore !== '\n') {
          const newline = document.createTextNode('\n');
          const newlineRange = document.createRange();
          newlineRange.setStart(node, startOffset);
          newlineRange.collapse(true);
          newlineRange.insertNode(newline);
          range.setStartAfter(newline);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && startOffset > 0) {
      const prevNode = node.childNodes[startOffset - 1];
      if (
        prevNode &&
        prevNode.nodeType === Node.TEXT_NODE &&
        prevNode.textContent &&
        !prevNode.textContent.endsWith('\n')
      ) {
        const newline = document.createTextNode('\n');
        node.insertBefore(newline, node.childNodes[startOffset]);
        range.setStartAfter(newline);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  insertImageNode(
    {
      id: tempId,
      url: blobUrl,
      loading: true,
      progress: 0,
      uploadStatus: 'uploading',
      filename: file?.name || '',
      width: dims?.width,
      height: dims?.height,
    },
    insertionPoint
  );

  // Upload material
  await uploadMaterial([file], {
    tempIds: [tempId],
    onProgress: ({ tempId: cbTempId, progress, stage }) => {
      const id = cbTempId || tempId;
      if (!editorRef.value?.querySelector(`[data-type="image"][data-asset-id="${id}"]`)) return;
      if (stage === 'done') return;
      updateImageNode(id, { loading: true, progress, uploadStatus: 'uploading' });
    },
    onPick: (asset) => {
      if (!editorRef.value?.querySelector(`[data-type="image"][data-asset-id="${tempId}"]`)) {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch {}
        return;
      }
      updateImageNode(tempId, { ...asset, loading: false, progress: 100, uploadStatus: 'done' });
      addAssetToChatState(asset);
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {}
    },
    onError: (msg) => {
      // Keep the blob url (default) image node if upload fails
      if (editorRef.value?.querySelector(`[data-type="image"][data-asset-id="${tempId}"]`)) {
        updateImageNode(tempId, { loading: false, progress: 0, uploadStatus: 'failed' });
      }
      toast({
        title: 'Upload failed',
        description: msg || 'Material upload failed, please try again',
        variant: 'destructive',
      });
    },
  });
};

/**
 * Handle file drop from useEditorDrag
 */
function handleFileDrop(files, _insertionPoint) {
  if (!files || files.length === 0) return;

  Array.from(files).forEach(async (file) => {
    const isImage = file.type.startsWith('image/');
    const isDocument =
      [
        'text/plain',
        'application/pdf',
        'text/markdown',
        'text/x-markdown',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(file.type) ||
      file.name.toLowerCase().endsWith('.txt') ||
      file.name.toLowerCase().endsWith('.md') ||
      file.name.toLowerCase().endsWith('.doc') ||
      file.name.toLowerCase().endsWith('.docx');

    if (isImage) {
      await uploadImageOnly(file);
    } else if (isDocument) {
      await uploadDocumentOnly(file);
    } else {
      toast({
        title: 'Unsupported file',
        description: 'Only images and documents are supported',
        variant: 'destructive',
      });
    }
  });
}

/**
 * Replace image placeholder with image node (for Word paste)
 * Inserts image at the exact position of the placeholder
 */
const replaceImagePlaceholder = async (placeholderId, file) => {
  if (!editorRef.value) return;

  const textNodes = [];
  const walker = document.createTreeWalker(editorRef.value, NodeFilter.SHOW_TEXT, null, false);

  let node;
  while ((node = walker.nextNode())) {
    if (node.textContent.includes(placeholderId)) {
      textNodes.push(node);
    }
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent;
    const index = text.indexOf(placeholderId);

    if (index !== -1) {
      const beforeText = text.substring(0, index);
      const afterText = text.substring(index + placeholderId.length);

      const parent = textNode.parentNode;

      // Create image element with temp ID at placeholder position
      const dataURL = await readFileAsBlobURL(file);
      const dims = await resolveImageDimensions(dataURL);
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const imageContainer = createImageElement({
        id: tempId,
        url: dataURL,
        loading: true,
        filename: file?.name || '',
        width: dims?.width,
        height: dims?.height,
      });

      // Insert before text if exists
      if (beforeText) {
        const beforeNode = document.createTextNode(beforeText);
        parent.insertBefore(beforeNode, textNode);
      }

      // Insert image element at placeholder position (before the original textNode)
      parent.insertBefore(imageContainer, textNode);

      // Add space after image
      const space = document.createTextNode('\u00A0');
      parent.insertBefore(space, textNode);

      // Insert after text if exists
      if (afterText) {
        const afterNode = document.createTextNode(afterText);
        parent.insertBefore(afterNode, textNode);
      }

      // Remove the original text node containing the placeholder
      parent.removeChild(textNode);

      // Upload the image and update the node
      await uploadMaterial([file], {
        tempIds: [tempId],
        onProgress: ({ tempId: cbTempId, progress, stage }) => {
          const id = cbTempId || tempId;
          if (!editorRef.value?.querySelector(`[data-type="image"][data-asset-id="${id}"]`)) return;
          if (stage === 'done') return;
          updateImageNode(id, { loading: true, progress, uploadStatus: 'uploading' });
        },
        onPick: (asset) => {
          if (!editorRef.value?.querySelector(`[data-type="image"][data-asset-id="${tempId}"]`)) {
            try {
              URL.revokeObjectURL(dataURL);
            } catch {}
            return;
          }
          updateImageNode(tempId, {
            ...asset,
            loading: false,
            progress: 100,
            uploadStatus: 'done',
          });
          addAssetToChatState(asset);
          try {
            URL.revokeObjectURL(dataURL);
          } catch {}
        },
        onError: (msg) => {
          if (editorRef.value?.querySelector(`[data-type="image"][data-asset-id="${tempId}"]`)) {
            updateImageNode(tempId, { loading: false, progress: 0, uploadStatus: 'failed' });
          }
          toast({
            title: 'Upload failed',
            description: msg || 'Material upload failed, please try again',
            variant: 'destructive',
          });
        },
      });

      break;
    }
  }
};

// Remove material (from editor and chatState)
const removeMaterial = (assetId) => {
  removeImageNodeById(assetId);
  removeAssetFromChatState(assetId);
};

const removeMaterialNode = (assetId) => {
  removeImageNodeById(assetId);
};

// Clipboard composable
const { handleCopy, handleCut, handlePaste } = useEditorClipboard({
  editorRef,
  syncContent: syncContentProxy,
  parseAndInsertText,
  insertImage,
  removeMaterial,
  replaceImagePlaceholder,
  toast,
  emit,
});

// ==================== Input and Keyboard Handlers ====================

/**
 * Handle input (optimized - reduced regex operations)
 */
const handleInput = () => {
  const selection = window.getSelection();
  if (!selection.rangeCount) {
    syncContentProxy();
    return;
  }

  const range = selection.getRangeAt(0);
  const textNode = range.startContainer;

  if (textNode.nodeType !== Node.TEXT_NODE) {
    showTagMenu.value = false;
    showCommandMenu.value = false;
    syncContentProxy();
    return;
  }

  const offset = range.startOffset;
  const text = textNode.textContent;
  const textBeforeCursor = text.slice(0, offset);

  // Quick check: if no special characters, skip expensive regex
  const lastChar = textBeforeCursor[textBeforeCursor.length - 1];
  const hasSpecialTrigger =
    textBeforeCursor.includes('#') ||
    textBeforeCursor.includes('/') ||
    lastChar === ' ' ||
    lastChar === ')';

  // Markdown: Bullet points (- ) -> •
  if (textBeforeCursor === '- ' || textBeforeCursor.endsWith('\n- ')) {
    const start = textBeforeCursor.lastIndexOf('- ');
    range.setStart(textNode, start);
    range.setEnd(textNode, offset);
    range.deleteContents();

    const bulletNode = document.createTextNode('• ');
    range.insertNode(bulletNode);

    range.setStartAfter(bulletNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    syncContentProxy();
    return;
  }

  if (!hasSpecialTrigger) {
    showTagMenu.value = false;
    showCommandMenu.value = false;
    syncContentProxy();
    return;
  }

  // Detect Remix Tag: @VeesoRemix('UUID', 'NAME')
  if (lastChar === ')') {
    const remixMatch = textBeforeCursor.match(REMIX_TAG_REGEX);
    if (remixMatch) {
      insertRemixTagFromInput(textNode, range, remixMatch);
      showCommandMenu.value = false;
      return;
    }

    const skillMatch = textBeforeCursor.match(USER_SKILL_TAG_REGEX);
    if (skillMatch) {
      insertUserSkillTagFromInputBase(textNode, range, skillMatch);
      showCommandMenu.value = false;
      return;
    }
  }

  // Detect HEX color input: #RGB or #RRGGBB followed by space
  if (lastChar === ' ' && textBeforeCursor.includes('#')) {
    const hexColorMatch = textBeforeCursor.match(HEX_COLOR_REGEX);
    if (hexColorMatch) {
      insertColorTagFromInput(textNode, range, hexColorMatch);
      showCommandMenu.value = false;
      return;
    }
  }

  // Detect Slash Command (/ followed by non-space)
  if (textBeforeCursor.includes('/')) {
    const slashMatch = textBeforeCursor.match(/\/([^\s/]*)$/);
    if (slashMatch) {
      showCommandMenu.value = true;
      commandQuery.value = slashMatch[1];
      selectedCommandIndex.value = 0;

      const coords = getCaretCoordinates();
      if (coords) {
        commandMenuPosition.value = coords;
      }

      showTagMenu.value = false;
      syncContentProxy();
      return;
    }
  }

  showCommandMenu.value = false;

  // Detect normal tag menu (# followed by non-color characters)
  if (textBeforeCursor.includes('#')) {
    const tagMatch = textBeforeCursor.match(/#([^\s#]*)$/);
    if (tagMatch) {
      const query = tagMatch[1];
      const isTypingHexColor = /^[0-9A-Fa-f]{0,6}$/.test(query);

      if (isTypingHexColor && query.length > 0) {
        showTagMenu.value = false;
      } else {
        showTagMenu.value = true;
        tagQuery.value = query;
        selectedTagIndex.value = 0;

        const coords = getCaretCoordinates();
        if (coords) {
          tagMenuPosition.value = coords;
        } else {
          tagMenuPosition.value = null;
        }
      }
    } else {
      showTagMenu.value = false;
    }
  } else {
    showTagMenu.value = false;
  }

  syncContentProxy();
};

/**
 * Handle keyboard events
 */
const handleKeydown = (e) => {
  // Handle undo/redo, ensure material state sync
  const isUndo = (e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey;
  const isRedo = (e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey));

  if (isUndo || isRedo) {
    // Use setTimeout(0) to run after browser's undo/redo completes
    setTimeout(() => syncContentProxy(), 0);
    return;
  }

  // Handle ArrowRight to stop before tags (fix jumping over tags)
  if (e.key === 'ArrowRight') {
    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      const { startContainer, startOffset } = range;

      if (startContainer.nodeType === Node.TEXT_NODE) {
        // If we are one character away from the end of the text node
        // and the next sibling is a contenteditable=false tag,
        // we want to stop AT the end of the text node (before the tag)
        // instead of letting the browser skip the tag.
        if (startOffset === startContainer.textContent.length - 1) {
          const nextNode = startContainer.nextSibling;
          if (
            nextNode &&
            nextNode.nodeType === Node.ELEMENT_NODE &&
            nextNode.hasAttribute('data-type') &&
            nextNode.contentEditable === 'false'
          ) {
            e.preventDefault();
            const newRange = document.createRange();
            newRange.setStart(startContainer, startOffset + 1);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            return;
          }
        }
      }
    }
  }

  // Handle ArrowLeft to stop after tags (fix jumping over tags from right)
  if (e.key === 'ArrowLeft') {
    const selection = window.getSelection();
    if (selection.rangeCount) {
      const range = selection.getRangeAt(0);
      const { startContainer, startOffset } = range;

      if (startContainer.nodeType === Node.TEXT_NODE) {
        // If we are one character away from the start of the text node
        // and the previous sibling is a contenteditable=false tag,
        // we want to stop AT the start of the text node (after the tag)
        // instead of letting the browser skip the tag.
        if (startOffset === 1) {
          const prevNode = startContainer.previousSibling;
          if (
            prevNode &&
            prevNode.nodeType === Node.ELEMENT_NODE &&
            prevNode.hasAttribute('data-type') &&
            prevNode.contentEditable === 'false'
          ) {
            e.preventDefault();
            const newRange = document.createRange();
            newRange.setStart(startContainer, startOffset - 1);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            return;
          }
        }
      }
    }
  }

  // Command Menu Navigation
  if (showCommandMenu.value && filteredCommands.value.length > 0) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedCommandIndex.value =
        (selectedCommandIndex.value - 1 + filteredCommands.value.length) %
        filteredCommands.value.length;
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedCommandIndex.value = (selectedCommandIndex.value + 1) % filteredCommands.value.length;
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleCommandSelect(filteredCommands.value[selectedCommandIndex.value]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      showCommandMenu.value = false;
      return;
    }
  }

  if (showTagMenu.value && availableTags.value.length > 0) {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedTagIndex.value =
        (selectedTagIndex.value - 1 + availableTags.value.length) % availableTags.value.length;
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedTagIndex.value = (selectedTagIndex.value + 1) % availableTags.value.length;
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertTag(availableTags.value[selectedTagIndex.value]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      showTagMenu.value = false;
      return;
    }
  }

  // Normal Enter key handling
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing && !showSkillSelector.value) {
    if (props.multiline) {
      // Allow natural line break — sync content after browser inserts the break
      nextTick(() => syncContentProxy());
    } else {
      e.preventDefault();
      emit('enter', e);
    }
    return;
  }
};

// ==================== Placeholder Management (Typewriter Effect) ====================

const TYPING_SPEED = 50; // ms per character
const PAUSE_AFTER_COMPLETE = 1500; // ms to pause after typing complete
const DELETE_SPEED = 25; // ms per character when deleting

const typeNextCharacter = () => {
  const labels = props.placeholderLabels;
  if (!labels || !labels.length) return;

  const currentLabel = labels[currentLabelIndex.value] || '';

  if (isTyping.value) {
    // Typing phase
    if (typedLabel.value.length < currentLabel.length) {
      typedLabel.value = currentLabel.substring(0, typedLabel.value.length + 1);
      typewriterTimeout = setTimeout(typeNextCharacter, TYPING_SPEED);
    } else {
      // Typing complete, pause then start deleting
      typewriterTimeout = setTimeout(() => {
        isTyping.value = false;
        typeNextCharacter();
      }, PAUSE_AFTER_COMPLETE);
    }
  } else {
    // Deleting phase
    if (typedLabel.value.length > 0) {
      typedLabel.value = typedLabel.value.substring(0, typedLabel.value.length - 1);
      typewriterTimeout = setTimeout(typeNextCharacter, DELETE_SPEED);
    } else {
      // Deletion complete, move to next label
      currentLabelIndex.value = (currentLabelIndex.value + 1) % labels.length;
      isTyping.value = true;
      typewriterTimeout = setTimeout(typeNextCharacter, TYPING_SPEED);
    }
  }
};

const startTypewriter = () => {
  if (typewriterTimeout) {
    clearTimeout(typewriterTimeout);
  }
  typedLabel.value = '';
  currentLabelIndex.value = 0;
  isTyping.value = true;
  typeNextCharacter();
};

const stopTypewriter = () => {
  if (typewriterTimeout) {
    clearTimeout(typewriterTimeout);
    typewriterTimeout = null;
  }
};

watch(
  () => props.placeholderLabels,
  () => {
    startTypewriter();
  },
  { deep: true }
);

// ==================== Watch and External Methods ====================

// Watch for external value changes
let isSettingValue = false;

watch(
  () => props.modelValue,
  (newValue) => {
    if (isSettingValue) return;
    if (newValue === lastSyncedValue.value) return;

    if (editorRef.value) {
      isSettingValue = true;
      lastSyncedValue.value = newValue || '';
      suppressExternalModelSync.value = true;
      try {
        setValue(newValue || '');
      } finally {
        suppressExternalModelSync.value = false;
      }
      isSettingValue = false;
    }
  }
);

watch(
  () => props.loading,
  (loading, prevLoading) => {
    if (prevLoading && !loading) {
      nextTick(() => adjustHeight());
    }
  }
);

/**
 * Insert existing material (from material library)
 */
const insertMaterial = (asset) => {
  if (!canAddMore('image')) {
    toast({
      title: 'Image limit reached',
      description: `You can add up to ${MAX_IMAGES} images`,
      variant: 'destructive',
    });
    return;
  }

  insertImageNode(asset);
  addAssetToChatState(asset, { checkDuplicate: true });
};

/**
 * Set editor content (for usePreset etc.)
 */
const setValue = (text) => {
  if (!editorRef.value) return;

  // Clear editor and associated assets
  editorRef.value.innerHTML = '';
  clearAllAssetsFromChatState();
  if (props.chatState) {
    props.chatState.theme = null;
    props.chatState.style = null;
    props.chatState.documents = [];
  }

  // If has content, use parseAndInsertText to parse special markers
  if (text) {
    editorRef.value.focus();
    parseAndInsertText(text, { skipSync: true, stateBehavior: 'replace' });

    // Move cursor to end
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      // Ignore cursor set failure
    }
  }

  // Update state without triggering emit (avoid loop)
  isEmpty.value = checkEmpty();
  adjustHeight();
};

const getValue = () => props.modelValue;

/**
 * Update material asset (used when upload completes to replace loading state)
 * Note: Only updates the editor DOM node. ChatState update is handled by the caller.
 */
const updateMaterialAsset = (tempId, newAsset) => {
  // Update the image node in editor
  updateImageNode(tempId, newAsset);
};

const insertDocumentFile = async (file) => {
  await uploadDocumentOnly(file);
};

const hasPendingUploads = () => {
  if (!editorRef.value) return false;
  const pendingImage =
    editorRef.value.querySelector('[data-type="image"][data-loading="true"]') ||
    editorRef.value.querySelector('[data-type="image"][data-upload-status="failed"]');
  const pendingDoc = editorRef.value.querySelector('[data-type="document"][data-loading="true"]');
  if (pendingImage || pendingDoc) return true;

  if (props.chatState?.assets?.some((a) => a.loading)) return true;
  if (props.chatState?.documents?.some((d) => d.loading)) return true;
  return false;
};

/**
 * Upload image file and add to chatState.assets only (no editor DOM insertion).
 * @param file - The file to upload
 * @param options - Optional settings
 * @param options.isMaterial - Whether the image is a material (true) or reference (false). Defaults to true.
 */
const uploadImageOnly = async (file, options = {}) => {
  if (!props.chatState) return;

  const isMaterial = options.isMaterial !== false;

  const currentImageCount = (props.chatState.assets || []).length;
  if (currentImageCount >= MAX_IMAGES) {
    toast({
      title: 'Image limit reached',
      description: `You can add up to ${MAX_IMAGES} images`,
      variant: 'destructive',
    });
    return;
  }

  if (
    !validateFile(file, 'material', (msg) =>
      toast({ title: 'Invalid file', description: msg, variant: 'destructive' })
    )
  )
    return;

  const blobUrl = URL.createObjectURL(file);
  const dims = await resolveImageDimensions(blobUrl);

  const tempId = `temp_${Date.now()}`;
  const tempAsset = {
    id: tempId,
    url: blobUrl,
    filename: file.name,
    isMaterial,
    loading: true,
    progress: 0,
    uploadStatus: 'uploading',
    width: dims?.width || 128,
    height: dims?.height || 128,
  };

  if (!props.chatState.assets) props.chatState.assets = [];
  props.chatState.assets.push(tempAsset);

  await uploadMaterial([file], {
    tempIds: [tempId],
    onProgress: ({ progress }) => {
      const asset = props.chatState.assets?.find((a) => a.id === tempId);
      if (asset) {
        asset.progress = progress;
      }
    },
    onPick: (asset) => {
      const idx = props.chatState.assets?.findIndex((a) => a.id === tempId);
      if (idx != null && idx > -1) {
        props.chatState.assets.splice(idx, 1, {
          ...asset,
          isMaterial,
          loading: false,
          progress: 100,
          uploadStatus: 'done',
          width: dims?.width || asset.width || 128,
          height: dims?.height || asset.height || 128,
        });
      }
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {}
    },
    onError: (msg) => {
      if (props.chatState?.assets) {
        props.chatState.assets = props.chatState.assets.filter((a) => a.id !== tempId);
      }
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {}
      toast({
        title: 'Upload failed',
        description: msg || 'Image upload failed, please try again',
        variant: 'destructive',
      });
    },
  });
};

/**
 * Upload document file and add to chatState.documents only (no editor DOM insertion).
 */
const uploadDocumentOnly = async (file) => {
  if (!props.chatState) return;

  const currentDocCount = (props.chatState.documents || []).length;
  if (currentDocCount >= MAX_DOCUMENTS) {
    toast({
      title: 'Document limit reached',
      description: `You can add up to ${MAX_DOCUMENTS} documents`,
      variant: 'destructive',
    });
    return;
  }

  if (
    !validateFile(file, 'document', (msg) =>
      toast({ title: 'Invalid file', description: msg, variant: 'destructive' })
    )
  )
    return;

  const tempId = `temp_doc_${Date.now()}`;
  if (!props.chatState.documents) props.chatState.documents = [];
  props.chatState.documents.push({
    id: tempId,
    filename: file.name,
    loading: true,
    parsed: false,
  });

  await uploadDocument([file], {
    onPick: (asset) => {
      const idx = props.chatState.documents?.findIndex((d) => d.id === tempId);
      if (idx != null && idx > -1) {
        const updatedDoc = {
          ...asset,
          loading: false,
        };
        props.chatState.documents.splice(idx, 1, updatedDoc);
        emit('document-uploaded', updatedDoc);
      }
    },
    onError: (msg) => {
      if (props.chatState?.documents) {
        props.chatState.documents = props.chatState.documents.filter((d) => d.id !== tempId);
      }
      toast({
        title: 'Upload failed',
        description: msg || 'Document upload failed, please try again',
        variant: 'destructive',
      });
    },
  });
};

// Expose methods to parent component
defineExpose({
  getFormattedContent,
  insertImage,
  insertMaterial,
  removeMaterial,
  removeMaterialNode,
  updateMaterialTag,
  updateMaterialAsset,
  insertTag,
  insertColorTag,
  insertDocumentTag,
  insertDocumentFile,
  updateDocumentNode,
  updateDocumentParsed,
  uploadImageOnly,
  uploadDocumentOnly,
  hasPendingUploads,
  isUploading,
  uploadProgress,
  getValue,
  setValue,
  focus: () => editorRef.value?.focus(),
  clear: () => {
    if (editorRef.value) {
      editorRef.value.innerHTML = '';
      clearAllAssetsFromChatState();
      syncContentProxy({ immediate: true });
    }
  },
});

// Handle custom events dispatched by updateMaterialNode after upload completes
const handleMaterialClickEvent = (e) => {
  const { data, element } = e.detail;
  handleMaterialClick(data, element);
};

const handleRemoveMaterialEvent = (e) => {
  const { id } = e.detail;
  removeAssetFromChatState(id);
  syncContentProxy({ immediate: true });
};

onMounted(() => {
  calculateMaxHeight();
  window.addEventListener('resize', calculateMaxHeight);

  // Initialize typewriter placeholder effect
  startTypewriter();

  // Initialize content
  if (props.modelValue && editorRef.value) {
    suppressExternalModelSync.value = true;
    try {
      setValue(props.modelValue);
    } finally {
      suppressExternalModelSync.value = false;
    }
  }
  isEmpty.value = checkEmpty();
  adjustHeight();

  // Listen for custom events from updated nodes (after upload)
  editorRef.value?.addEventListener('material-click', handleMaterialClickEvent);
  editorRef.value?.addEventListener('remove-material', handleRemoveMaterialEvent);
});

onUnmounted(() => {
  window.removeEventListener('resize', calculateMaxHeight);
  stopTypewriter();
  editorRef.value?.removeEventListener('material-click', handleMaterialClickEvent);
  editorRef.value?.removeEventListener('remove-material', handleRemoveMaterialEvent);
});
</script>

<template>
  <div class="editor-container relative">
    <!-- Command Menu -->
    <CommandMenu
      :show="showCommandMenu"
      :commands="filteredCommands"
      :selected-index="selectedCommandIndex"
      :position="commandMenuPosition"
      @select="handleCommandSelect"
      @hover="selectedCommandIndex = $event"
      @close="showCommandMenu = false"
    />

    <!-- Tag Selection Menu -->
    <TagMenu
      :show="showTagMenu"
      :tags="availableTags"
      :selected-index="selectedTagIndex"
      :position="tagMenuPosition"
      @select="insertTag"
      @hover="selectedTagIndex = $event"
      @close="showTagMenu = false"
    />

    <!-- Color Editor Popover -->
    <ColorPopover
      :show="showColorPopover"
      :color-id="colorPopoverData.id"
      :color-value="colorPopoverData.value"
      :position="colorPopoverData.position"
      @update="handleColorUpdate"
      @delete="handleColorDelete"
      @close="showColorPopover = false"
    />

    <!-- Material Editor Popover -->
    <MaterialPopover
      :show="showMaterialPopover"
      :asset-id="materialPopoverData.id"
      :asset-url="materialPopoverData.url"
      :current-tag="materialPopoverData.tag"
      :is-material="materialPopoverData.isMaterial"
      :position="materialPopoverData.position"
      @update="handleMaterialUpdate"
      @close="showMaterialPopover = false"
    />

    <SkillPopover
      :show="showSkillPopover"
      :skill-id="skillPopoverData.id"
      :position="skillPopoverData.position"
      @remove="handleSkillRemove"
      @close="showSkillPopover = false"
    />

    <!-- Skill Selector Popover -->
    <SkillSelectorPopover
      :show="showSkillSelector"
      :position="skillSelectorPosition"
      @select="handleSkillSelect"
      @close="showSkillSelector = false"
    />

    <!-- Rich Text Editor -->
    <div
      ref="editorRef"
      contenteditable="true"
      :class="
        cn(
          'editor-content relative w-full px-5 py-4 text-neutral-800 minimal-scrollbar resize-none focus:outline-none bg-transparent overflow-y-auto leading-relaxed',
          props.class
        )
      "
      :style="{ minHeight: `${props.minHeight}px`, maxHeight: `${maxHeight}px` }"
      @input="handleInput"
      @copy="handleCopy"
      @cut="handleCut"
      @paste="handlePaste"
      @keydown="handleKeydown"
      @dragstart="handleDragStart"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
      @dragend="handleDragEnd"
      @focus="
        isFocused = true;
        emit('focus');
      "
      @blur="
        isFocused = false;
        emit('blur');
      "
      @scroll="
        showColorPopover = false;
        showMaterialPopover = false;
      "
    ></div>

    <!-- File Drop Zone Overlay -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="dragState.isExternalDragging"
        class="absolute inset-0 z-10 pointer-events-none flex items-center justify-center rounded-[inherit]"
      >
        <div class="absolute inset-0 bg-white/60 backdrop-blur-[2px] rounded-[inherit]"></div>
        <div class="absolute inset-[6px] border-2 border-dashed border-black/15 rounded-2xl"></div>
        <div class="relative flex flex-col items-center gap-1.5">
          <div class="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-black/40"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <span class="text-xs font-medium text-black/40">Drop files here</span>
        </div>
      </div>
    </Transition>

    <!-- Placeholder (no leave transition when focused to avoid lag) -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-0.5"
      enter-to-class="opacity-100 translate-y-0"
      :leave-active-class="isFocused ? '' : 'transition-all duration-200 ease-in'"
      :leave-from-class="isFocused ? '' : 'opacity-100'"
      :leave-to-class="isFocused ? '' : 'opacity-0'"
    >
      <div
        v-if="isEmpty && !loading"
        :class="
          cn(
            'placeholder-text absolute top-4 left-5 text-neutral-400 pointer-events-none select-none font-normal flex items-center',
            props.placeholderClass
          )
        "
      >
        <span>{{ placeholderPrefix }}</span>
        <span class="ml-1">{{ typedLabel }}</span>
      </div>
    </Transition>

    <!-- Loading State -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="loading"
        class="absolute top-4 left-5 flex items-center gap-2.5 text-neutral-400 pointer-events-none select-none"
      >
        <span class="loading-dots flex gap-1">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </span>
        <span class="text-[13px]">Please wait a moment while your design is being created...</span>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Editor Container */
.editor-container {
  position: relative;
}

/* Editor Content - Apple Typography */
.editor-content {
  font-size: 16px;
  line-height: 1.7;
  letter-spacing: -0.01em;
  font-feature-settings:
    'kern' 1,
    'liga' 1;
  caret-color: #171717;
  word-wrap: break-word;
  white-space: pre-wrap;
  text-align: left;
}

.editor-content:focus {
  outline: none;
}

.editor-content::selection {
  background: rgba(32, 246, 218, 0.2);
}

.editor-content::-moz-selection {
  background: rgba(32, 246, 218, 0.2);
}

/* Placeholder */
.placeholder-text {
  font-size: 16px;
  line-height: 1.7;
  letter-spacing: -0.01em;
}

/* Typewriter Cursor */
.typewriter-cursor {
  animation: blink 1s step-end infinite;
  margin-left: 1px;
  font-weight: 300;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

/* Minimal Scrollbar */
.minimal-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.minimal-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.minimal-scrollbar::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 2px;
  transition: background 0.2s ease;
}

.minimal-scrollbar:hover::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
}

.minimal-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

/* Loading Dots Animation */
.loading-dots {
  display: flex;
  align-items: center;
  gap: 3px;
}

.loading-dots .dot {
  width: 4px;
  height: 4px;
  background: currentColor;
  border-radius: 50%;
  animation: dot-pulse 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-pulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Draggable Items */
:deep(.draggable-item) {
  cursor: grab;
  transition:
    transform 0.15s cubic-bezier(0.32, 0.72, 0, 1),
    box-shadow 0.15s cubic-bezier(0.32, 0.72, 0, 1),
    opacity 0.15s ease;
}

:deep(.draggable-item:hover) {
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.04);
}

:deep(.draggable-item.dragging) {
  opacity: 0.7;
  cursor: grabbing;
  transform: scale(1.01);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08);
}

:deep(.draggable-item:active) {
  cursor: grabbing;
}

/* Image Container Styles */
:deep([data-type='image']) {
  vertical-align: top;
}

:deep([data-type='image'] img) {
  display: block;
}

:deep(.img-wrapper) {
  min-width: 100px;
  min-height: 72px;
  max-width: 400px;
  display: flex;
}

/* Drop Indicator */
:deep(.drop-indicator) {
  display: inline-block;
  width: 2px;
  height: 20px;
  background: linear-gradient(180deg, #20f6da 0%, #86eeba 100%);
  border-radius: 1px;
  margin: 0 1px;
  vertical-align: middle;
  animation: indicator-pulse 1s ease-in-out infinite;
}

@keyframes indicator-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Resize Handles - Modern minimal design */
:deep(.resize-handles) {
  z-index: 15;
}

:deep([class^='resize-handle-']) {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: all 0.15s ease;
}

:deep([class^='resize-handle-']:hover) {
  transform: scale(1.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

:deep([class^='resize-handle-']:active) {
  transform: scale(1.1);
}

/* Image Selection State */
:deep(.img-wrapper.border-neutral-900) {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
}

/* Material Node */
:deep(.material-node) {
  transition: all 0.2s ease;
}

:deep(.material-node:hover .img-wrapper) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Usage Trigger - Bottom button on material */
:deep(.usage-trigger) {
  transition: all 0.2s ease;
}

/* Tag Chips */
:deep([data-type='tag']) {
  transition: all 0.15s ease;
}

:deep([data-type='tag']:hover) {
  filter: brightness(0.97);
}

/* Color Tags */
:deep(.color-tag) {
  transition: all 0.12s cubic-bezier(0.32, 0.72, 0, 1);
}

:deep(.color-tag:hover) {
  filter: brightness(0.95);
}
</style>
